/**
 * The deck's event surface, and the plumbing that forwards an engine's events
 * onto it — but only while that engine is the one actually playing.
 */

import type { Track } from '../types/media.ts';
import type { Engine } from './engine.ts';

export type DeckEvents = {
  timeupdate: [position: number, duration: number];
  durationchange: [duration: number];
  trackchange: [track: Track, index: number];
  queue: [queue: readonly Track[], index: number];
  play: [];
  pause: [];
  ended: [track: Track | null];
  /** The engine reached the end of a side and the deck wants the next record. */
  wantnext: [];
  volume: [volume: number, muted: boolean];
  error: [message: string];
};

export interface EngineBridge {
  /** True while this engine owns the deck. Events from the other one are dropped. */
  active: () => boolean;
  time: (position: number) => void;
  duration: (seconds: number) => void;
  play: () => void;
  pause: () => void;
  ended: () => void;
  error: (message: string) => void;
}

export function bridgeEngine(engine: Engine, b: EngineBridge): void {
  const gate =
    <T extends unknown[]>(fn: (...a: T) => void) =>
    (...a: T) => {
      if (b.active()) fn(...a);
    };

  engine.on('timeupdate', gate(b.time));
  engine.on('durationchange', gate(b.duration));
  engine.on('play', gate(b.play));
  engine.on('pause', gate(b.pause));
  engine.on('ended', gate(b.ended));
  engine.on('error', gate(b.error));
}
